import { Component, Input, OnInit } from "@angular/core";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart, registerables } from "chart.js";
import { VoyageReportWeatherData } from "@app/services/api/api.models";

@Component({
  selector: "app-weather-at-sea-diagram",
  templateUrl: "./weather-at-sea-diagram.component.html",
  styleUrls: ["./weather-at-sea-diagram.component.scss"],
})
export class WeatherAtSeaDiagramComponent implements OnInit {
  @Input() weatherAtSeaValues: VoyageReportWeatherData;

  weatherAtSeaChartData: any;
  weatherAtSeaChartOptions: any;
  weatherAtSeaChartPlugins: any;

  ngOnInit(): void {
    Chart.register(...registerables);

    const originalGood = this.weatherAtSeaValues.weatherAtSeaGood_ratio * 100;
    const originalBad = this.weatherAtSeaValues.weatherAtSeaBad_ratio * 100;

    const total = originalGood + originalBad;

    const goodNormalized = total === 0 ? 0 : (originalGood / total) * 100;
    const badNormalized = total === 0 ? 0 : (originalBad / total) * 100;

    this.weatherAtSeaChartData = {
      labels: ["Weather at Sea"],
      datasets: [
        {
          label: "Good (0–4 Bft)",
          data: [goodNormalized],
          backgroundColor: "#C6DEAB",
          hoverBackgroundColor: ["#9BC56D"],
          originalValue: originalGood,

          barThickness: 60,
          borderRadius:
            originalBad === 0
              ? {
                  topLeft: 10,
                  bottomLeft: 10,
                  topRight: 10,
                  bottomRight: 10,
                }
              : {
                  topLeft: 10,
                  bottomLeft: 10,
                  topRight: 0,
                  bottomRight: 0,
                },
          borderSkipped: false,
          barPercentage: 1,
          categoryPercentage: 1,
        },
        {
          label: "Bad (5–12 Bft)",
          data: [badNormalized],
          backgroundColor: "#EE9DA3",
          hoverBackgroundColor: ["#E1535D"],
          originalValue: originalBad,

          barThickness: 60,
          borderRadius:
            originalGood === 0
              ? {
                  topLeft: 10,
                  bottomLeft: 10,
                  topRight: 10,
                  bottomRight: 10,
                }
              : {
                  topLeft: 0,
                  bottomLeft: 0,
                  topRight: 10,
                  bottomRight: 10,
                },
          borderSkipped: false,
          barPercentage: 1,
          categoryPercentage: 1,
        },
      ],
    };

    this.weatherAtSeaChartOptions = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 0,
          bottom: 30,
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "rectRounded",
            boxWidth: 16,
            boxHeight: 16,
            padding: 16,
            font: {
              family: "Heebo, sans-serif",
              size: 12,
            },
          },
        },
        tooltip: {
          enabled: true,
          cornerRadius: 4,
          padding: 10,
          displayColors: false,
          color: "000",
          callbacks: {
            label: (tooltipItem) => {
              const dataset = tooltipItem.dataset as any;
              const original = dataset.originalValue ?? tooltipItem.raw;
              return `${tooltipItem.dataset.label}: ${original.toFixed(0)} %`;
            },
          },
        },
        datalabels: {
          color: "#000",
          clamp: true,
          anchor: "center",
          align: (ctx) => {
            const value = (ctx.dataset as any).originalValue ?? ctx.raw;
            const label = ctx.dataset.label;
            if (label.includes("Good")) {
              return value <= total / 11 ? "start" : "center";
            }
            if (label.includes("Bad")) {
              return value <= total / 11 ? "end" : "center";
            }
          },
          offset: (ctx) => {
            const value = (ctx.dataset as any).originalValue ?? ctx.raw;
            const maxOffset = 45;
            const ratio = value / total;
            const label = ctx.dataset.label;
            const offset = -maxOffset * Math.pow(1 - ratio * 8, 0.15);
            if (ratio <= 1 / 11) {
              if (label.includes("Good")) {
                return offset;
              }
              if (label.includes("Bad")) {
                return offset + 25;
              }
            }
            return 0;
          },
          formatter: (value, context) => {
            const original = (context.dataset as any).originalValue ?? value;
            if (original === 0) {
              return "";
            }
            return `${original.toFixed(0)} % `;
          },
        },
      },
      scales: {
        x: {
          min: 0,
          max: 100,
          stacked: true,
          display: false,
        },
        y: {
          stacked: true,
          display: false,
        },
      },
    };

    this.weatherAtSeaChartPlugins = [ChartDataLabels];
  }
}
